import type { RPlivoApplication } from 'v2/types/api/settings'
import { ApplicationType, type XmlApplicationFormValues } from './types'

/**
 * The Edit-Application sheet has two data sources for the same app:
 *   - `list`   — the internal list row that opened the sheet (reliable; fields populated)
 *   - `detail` — GET /plivo-api/Application/{id}/ (freshest, but for some apps returns
 *                `null` for app_name / default_app / default_endpoint_app / enabled / …)
 *
 * The form used to prefer `detail` outright, so those nulls flowed straight into the
 * strict Zod schema (`z.string()` / `z.boolean()`) and blocked editing an app the
 * customer could plainly see — "Expected string, received null" / "…boolean, received
 * null" (verified: app 27523914372648084, detail returned app_name/default_app/
 * default_endpoint_app = null while the list returned real values).
 *
 * Merge with `list` as the base and let `detail` override ONLY where it is non-null.
 * This keeps real values (so a null from `detail` can never blank the saved name), while
 * still picking up any fresher value `detail` actually carries.
 */
const mergeApplicationSources = (
  list: RPlivoApplication,
  detail?: RPlivoApplication | null,
): RPlivoApplication => {
  if (!detail) return list
  const nonNullDetail = Object.fromEntries(
    Object.entries(detail).filter(([, value]) => value !== null && value !== undefined),
  )
  return { ...list, ...nonNullDetail } as RPlivoApplication
}

/**
 * Build the Edit-Application form default values. Prefers the reliable list row, layers
 * on any non-null detail values, and coalesces the schema-critical fields as a final
 * safety net so the form can never be handed a `null` (`z.boolean().default(false)` only
 * substitutes `undefined`, not `null`).
 */
export const buildApplicationFormDefaults = (
  list: RPlivoApplication,
  detail?: RPlivoApplication | null,
): XmlApplicationFormValues => {
  const appData = mergeApplicationSources(list, detail)

  const subaccount = appData.sub_account
    ? // Extract auth_id from "/v1/Internal/SAZ.../" or "/v1/Account/..." format
      (appData.sub_account.match(/\/([^/]+)\/?$/)?.[1] ?? appData.sub_account)
    : undefined

  return {
    application_type: ApplicationType.XML,
    app_name: appData.app_name ?? '',
    name: appData.app_name ?? '',
    answer_url: appData.answer_url ?? '',
    answer_method: (appData.answer_method as 'GET' | 'POST') || 'POST',
    hangup_url: appData.hangup_url ?? '',
    hangup_method: (appData.hangup_method as 'GET' | 'POST') || 'POST',
    message_url: appData.message_url ?? '',
    message_method: (appData.message_method as 'GET' | 'POST') || 'POST',
    fallback_answer_url: appData.fallback_answer_url ?? '',
    fallback_method: (appData.fallback_method as 'GET' | 'POST') || 'POST',
    default_number_app: appData.default_app ?? false,
    default_endpoint_app:
      'default_endpoint_app' in appData
        ? ((appData.default_endpoint_app as boolean | null | undefined) ?? false)
        : false,
    log_incoming_messages: appData.log_incoming_messages ?? false,
    subaccount,
    public_uri:
      'public_uri' in appData
        ? ((appData.public_uri as boolean | null | undefined) ?? false)
        : false,
  }
}

/**
 * Translate the SubAccountsSelect value into what trace's /plivo-api/Application/
 * endpoints accept (application_repository.go):
 *   - key absent      → leave the existing subaccount mapping untouched
 *   - empty string    → unlink the app from its subaccount (update only)
 *   - anything else   → must be a real subaccount auth_id, otherwise trace 400s with
 *                       "Given sub account does not belong to the main account"
 *
 * The "----" option carries the old-console sentinel 'Nil', which the old console's
 * Django layer stripped before calling the API. We call trace directly, so the sentinel
 * must be translated here: omit it on create, send '' (explicit unlink) on edit.
 */
export const resolveSubaccountPayload = (
  subaccount: string | undefined,
  isEditing: boolean,
): string | undefined => {
  if (subaccount && subaccount !== 'Nil') return subaccount
  return isEditing ? '' : undefined
}
